import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  EnvelopeSimple,
  ChatCircle,
  Users,
  Eye,
  CheckCircle,
  Clock,
  ArrowClockwise,
  SignOut,
  Newspaper,
  Plus,
  PencilSimple,
  Trash,
  X,
} from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const newsCategories = ['General', 'Technology', 'Company News', 'Industry Insights', 'Product Updates'];

export default function AdminPage() {
  const { theme } = useTheme();
  const { user, isAuthenticated, isLoading: authLoading, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [chats, setChats] = useState([]);
  const [news, setNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // News form state
  const [showNewsDialog, setShowNewsDialog] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsForm, setNewsForm] = useState({
    title: '',
    summary: '',
    content: '',
    image_url: '',
    category: 'General',
    published: true,
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeader();
      const [statsRes, contactsRes, newslettersRes, chatsRes, newsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/newsletter`, { headers }),
        axios.get(`${API}/admin/chat`, { headers }),
        axios.get(`${API}/admin/news`, { headers }),
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setNewsletters(newslettersRes.data);
      setChats(chatsRes.data);
      setNews(newsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeader, logout, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const updateContactStatus = async (contactId, status) => {
    try {
      await axios.patch(`${API}/admin/contact/${contactId}/status?status=${status}`, {}, {
        headers: getAuthHeader()
      });
      setContacts(prev => 
        prev.map(c => c.id === contactId ? { ...c, status } : c)
      );
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    if (!newsForm.title || !newsForm.summary || !newsForm.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const headers = getAuthHeader();
      if (editingNews) {
        await axios.put(`${API}/admin/news/${editingNews.id}`, newsForm, { headers });
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/admin/news`, newsForm, { headers });
        toast.success('Article created');
      }
      setShowNewsDialog(false);
      resetNewsForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  const deleteNews = async (newsId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await axios.delete(`${API}/admin/news/${newsId}`, { headers: getAuthHeader() });
      toast.success('Article deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const resetNewsForm = () => {
    setNewsForm({
      title: '',
      summary: '',
      content: '',
      image_url: '',
      category: 'General',
      published: true,
    });
    setEditingNews(null);
  };

  const openEditNews = (article) => {
    setEditingNews(article);
    setNewsForm({
      title: article.title,
      summary: article.summary,
      content: article.content,
      image_url: article.image_url || '',
      category: article.category,
      published: article.published,
    });
    setShowNewsDialog(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-[#00C8EB]/20 text-[#00C8EB] border-[#00C8EB]/30',
      contacted: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
      closed: 'bg-green-500/20 text-green-500 border-green-500/30',
      unread: 'bg-[#00C8EB]/20 text-[#00C8EB] border-[#00C8EB]/30',
    };
    return styles[status] || styles.new;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (authLoading || isLoading) {
    return (
      <div data-testid="admin-page" className={`min-h-screen pt-24 flex items-center justify-center ${
        theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'
      }`}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00C8EB] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div data-testid="admin-page" className={`min-h-screen pt-24 ${
      theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Admin Dashboard
            </h1>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              Welcome back, {user?.name}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={fetchData}
              data-testid="admin-refresh-btn"
              variant="outline"
              className={theme === 'dark' ? 'border-[#464B8B]/30' : ''}
            >
              <ArrowClockwise size={18} className="mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              variant="outline"
              className={`text-red-500 border-red-500/30 hover:bg-red-500/10 ${theme === 'dark' ? '' : ''}`}
            >
              <SignOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total Contacts', value: stats?.total_contacts || 0, icon: EnvelopeSimple, color: '#464B8B' },
            { label: 'New Inquiries', value: stats?.new_contacts || 0, icon: Eye, color: '#00C8EB' },
            { label: 'Newsletter Subs', value: stats?.newsletter_subscribers || 0, icon: Users, color: '#464B8B' },
            { label: 'Unread Chats', value: stats?.unread_chats || 0, icon: ChatCircle, color: '#00C8EB' },
            { label: 'News Articles', value: stats?.total_news || 0, icon: Newspaper, color: '#464B8B' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`admin-stat-${index}`}
              className={`p-5 rounded-xl ${
                theme === 'dark' 
                  ? 'bg-[#12131F] border border-[#464B8B]/20' 
                  : 'bg-white shadow-sm'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {stat.label}
                  </p>
                  <p className={`text-2xl font-bold mt-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {stat.value}
                  </p>
                </div>
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <stat.icon size={20} weight="duotone" style={{ color: stat.color }} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="contacts" className="space-y-6">
          <TabsList className={`${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-100'}`}>
            <TabsTrigger value="contacts" data-testid="tab-contacts">
              Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="tab-newsletter">
              Newsletter ({newsletters.length})
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat">
              Chat ({chats.length})
            </TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news">
              News ({news.length})
            </TabsTrigger>
          </TabsList>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className={`rounded-xl overflow-hidden ${
              theme === 'dark' ? 'bg-[#12131F] border border-[#464B8B]/20' : 'bg-white shadow-sm'
            }`}>
              <Table>
                <TableHeader>
                  <TableRow className={theme === 'dark' ? 'border-[#464B8B]/20' : ''}>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Name</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Email</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Service</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Date</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Status</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                          No contact submissions yet
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    contacts.map((contact) => (
                      <TableRow 
                        key={contact.id} 
                        data-testid={`contact-row-${contact.id}`}
                        className={theme === 'dark' ? 'border-[#464B8B]/20' : ''}
                      >
                        <TableCell className={theme === 'dark' ? 'text-white' : ''}>
                          <div>
                            <p className="font-medium">{contact.name}</p>
                            {contact.company && (
                              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                {contact.company}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className={theme === 'dark' ? 'text-gray-300' : ''}>
                          {contact.email}
                        </TableCell>
                        <TableCell className={theme === 'dark' ? 'text-gray-300' : ''}>
                          {contact.service_interest}
                        </TableCell>
                        <TableCell className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                          {formatDate(contact.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusBadge(contact.status)} border`}>
                            {contact.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {contact.status === 'new' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateContactStatus(contact.id, 'contacted')}
                                data-testid={`mark-contacted-${contact.id}`}
                                className={theme === 'dark' ? 'border-[#464B8B]/30 text-xs' : 'text-xs'}
                              >
                                <Clock size={14} className="mr-1" />
                                Contacted
                              </Button>
                            )}
                            {contact.status !== 'closed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateContactStatus(contact.id, 'closed')}
                                data-testid={`mark-closed-${contact.id}`}
                                className={`text-xs ${theme === 'dark' ? 'border-green-500/30 text-green-500' : 'text-green-600'}`}
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Close
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Newsletter Tab */}
          <TabsContent value="newsletter">
            <div className={`rounded-xl overflow-hidden ${
              theme === 'dark' ? 'bg-[#12131F] border border-[#464B8B]/20' : 'bg-white shadow-sm'
            }`}>
              <Table>
                <TableHeader>
                  <TableRow className={theme === 'dark' ? 'border-[#464B8B]/20' : ''}>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Email</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Subscribed Date</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {newsletters.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8">
                        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                          No newsletter subscribers yet
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    newsletters.map((sub) => (
                      <TableRow 
                        key={sub.id} 
                        data-testid={`newsletter-row-${sub.id}`}
                        className={theme === 'dark' ? 'border-[#464B8B]/20' : ''}
                      >
                        <TableCell className={theme === 'dark' ? 'text-white' : ''}>
                          {sub.email}
                        </TableCell>
                        <TableCell className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                          {formatDate(sub.subscribed_at)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${sub.active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'} border`}>
                            {sub.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat">
            <div className={`rounded-xl overflow-hidden ${
              theme === 'dark' ? 'bg-[#12131F] border border-[#464B8B]/20' : 'bg-white shadow-sm'
            }`}>
              <Table>
                <TableHeader>
                  <TableRow className={theme === 'dark' ? 'border-[#464B8B]/20' : ''}>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Name</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Email</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Message</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chats.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8">
                        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                          No chat messages yet
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    chats.map((chat) => (
                      <TableRow 
                        key={chat.id} 
                        data-testid={`chat-row-${chat.id}`}
                        className={theme === 'dark' ? 'border-[#464B8B]/20' : ''}
                      >
                        <TableCell className={theme === 'dark' ? 'text-white' : ''}>
                          {chat.name}
                        </TableCell>
                        <TableCell className={theme === 'dark' ? 'text-gray-300' : ''}>
                          {chat.email}
                        </TableCell>
                        <TableCell className={`max-w-xs truncate ${theme === 'dark' ? 'text-gray-300' : ''}`}>
                          {chat.message}
                        </TableCell>
                        <TableCell className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                          {formatDate(chat.created_at)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            <div className="mb-4">
              <Button
                onClick={() => {
                  resetNewsForm();
                  setShowNewsDialog(true);
                }}
                data-testid="add-news-btn"
                className="bg-[#00C8EB] hover:bg-[#00C8EB]/90 text-[#0A0A10]"
              >
                <Plus size={18} className="mr-2" />
                Add Article
              </Button>
            </div>
            <div className={`rounded-xl overflow-hidden ${
              theme === 'dark' ? 'bg-[#12131F] border border-[#464B8B]/20' : 'bg-white shadow-sm'
            }`}>
              <Table>
                <TableHeader>
                  <TableRow className={theme === 'dark' ? 'border-[#464B8B]/20' : ''}>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Title</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Category</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Status</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Date</TableHead>
                    <TableHead className={theme === 'dark' ? 'text-gray-300' : ''}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {news.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                          No news articles yet
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    news.map((article) => (
                      <TableRow 
                        key={article.id} 
                        data-testid={`news-row-${article.id}`}
                        className={theme === 'dark' ? 'border-[#464B8B]/20' : ''}
                      >
                        <TableCell className={theme === 'dark' ? 'text-white' : ''}>
                          <p className="font-medium max-w-xs truncate">{article.title}</p>
                        </TableCell>
                        <TableCell className={theme === 'dark' ? 'text-gray-300' : ''}>
                          {article.category}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${article.published ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'} border`}>
                            {article.published ? 'Published' : 'Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                          {formatDate(article.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditNews(article)}
                              data-testid={`edit-news-${article.id}`}
                              className={theme === 'dark' ? 'border-[#464B8B]/30' : ''}
                            >
                              <PencilSimple size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteNews(article.id)}
                              data-testid={`delete-news-${article.id}`}
                              className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                            >
                              <Trash size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* News Dialog */}
      <Dialog open={showNewsDialog} onOpenChange={setShowNewsDialog}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${
          theme === 'dark' ? 'bg-[#12131F] border-[#464B8B]/30' : ''
        }`}>
          <DialogHeader>
            <DialogTitle className={theme === 'dark' ? 'text-white' : ''}>
              {editingNews ? 'Edit Article' : 'Create New Article'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleNewsSubmit} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Title *
              </label>
              <Input
                placeholder="Article title"
                value={newsForm.title}
                onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                data-testid="news-title-input"
                className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Summary *
              </label>
              <Textarea
                placeholder="Brief summary of the article"
                value={newsForm.summary}
                onChange={(e) => setNewsForm({ ...newsForm, summary: e.target.value })}
                data-testid="news-summary-input"
                rows={2}
                className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Content *
              </label>
              <Textarea
                placeholder="Full article content"
                value={newsForm.content}
                onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })}
                data-testid="news-content-input"
                rows={6}
                className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Category
                </label>
                <Select
                  value={newsForm.category}
                  onValueChange={(value) => setNewsForm({ ...newsForm, category: value })}
                >
                  <SelectTrigger 
                    data-testid="news-category-select"
                    className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {newsCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Status
                </label>
                <Select
                  value={newsForm.published ? 'published' : 'draft'}
                  onValueChange={(value) => setNewsForm({ ...newsForm, published: value === 'published' })}
                >
                  <SelectTrigger 
                    data-testid="news-status-select"
                    className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Image URL (optional)
              </label>
              <Input
                placeholder="https://example.com/image.jpg"
                value={newsForm.image_url}
                onChange={(e) => setNewsForm({ ...newsForm, image_url: e.target.value })}
                data-testid="news-image-input"
                className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewsDialog(false)}
                className={theme === 'dark' ? 'border-[#464B8B]/30' : ''}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="news-submit-btn"
                className="bg-[#00C8EB] hover:bg-[#00C8EB]/90 text-[#0A0A10]"
              >
                {editingNews ? 'Update Article' : 'Create Article'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
